/**
 * Snowcubes Reviews JSON-LD
 * - AggregateRating structured-data build + upsert, split out of reviews-core.
 * - Browser global: attaches its helpers onto window.SnowcubesReviews.
 * - Load order: this file MUST load BEFORE reviews-core.js (core re-exports
 *   these helpers on its public api object so callers keep resolving them).
 */
(function initSnowcubesReviewsJsonLd(global) {
  'use strict';

  function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  function normalizeString(value, fallback) {
    if (value === null || value === undefined) return fallback || '';
    var str = String(value).trim();
    return str || fallback || '';
  }

  function safeJsonParse(raw) {
    try {
      return { ok: true, value: JSON.parse(raw) };
    } catch (error) {
      return { ok: false, error: error };
    }
  }

  function buildAggregateRating(summary) {
    var count = Number(summary && summary.reviewCount);
    var rating = Number(summary && summary.averageRating);
    if (!Number.isFinite(count) || count < 1 || !Number.isFinite(rating) || rating < 1) {
      return null;
    }
    return {
      '@type': 'AggregateRating',
      ratingValue: String(Math.round(rating * 10) / 10),
      reviewCount: String(Math.floor(count)),
      bestRating: '5',
      worstRating: '1',
    };
  }

  function nodeHasType(node, typeName) {
    if (!isPlainObject(node)) return false;
    var type = node['@type'];
    if (Array.isArray(type)) return type.indexOf(typeName) !== -1;
    return type === typeName;
  }

  function containsAggregateRating(value) {
    if (Array.isArray(value)) return value.some(containsAggregateRating);
    if (!isPlainObject(value)) return false;
    if (value.aggregateRating) return true;
    return Object.keys(value).some(function (key) {
      return containsAggregateRating(value[key]);
    });
  }

  function injectAggregateIntoProduct(value, aggregateRating) {
    if (Array.isArray(value)) {
      return value.some(function (entry) {
        return injectAggregateIntoProduct(entry, aggregateRating);
      });
    }
    if (!isPlainObject(value)) return false;
    if (nodeHasType(value, 'Product')) {
      value.aggregateRating = aggregateRating;
      return true;
    }
    if (Array.isArray(value['@graph'])) {
      return injectAggregateIntoProduct(value['@graph'], aggregateRating);
    }
    return false;
  }

  function upsertAggregateJsonLd(options) {
    var opts = options || {};
    var doc = opts.document || global.document;
    var aggregateRating = buildAggregateRating(opts.summary);
    if (!aggregateRating) return { updated: false, reason: 'empty_summary' };
    if (!doc || !doc.querySelectorAll) return { updated: false, reason: 'missing_document' };

    var scripts = Array.prototype.slice.call(
      doc.querySelectorAll('script[type="application/ld+json"]'),
    );
    for (var index = 0; index < scripts.length; index += 1) {
      var existing = safeJsonParse(scripts[index].textContent || '');
      if (existing.ok && containsAggregateRating(existing.value)) {
        return { updated: false, reason: 'existing_aggregate' };
      }
    }

    for (var productIndex = 0; productIndex < scripts.length; productIndex += 1) {
      var parsed = safeJsonParse(scripts[productIndex].textContent || '');
      if (!parsed.ok) continue;
      if (injectAggregateIntoProduct(parsed.value, aggregateRating)) {
        scripts[productIndex].textContent = JSON.stringify(parsed.value);
        return { updated: true, mode: 'augmented' };
      }
    }

    var product = opts.product || {};
    var node = doc.createElement('script');
    node.type = 'application/ld+json';
    node.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': normalizeString(product.url || product.id || product.handle, '#product'),
      name: normalizeString(product.title || product.name, 'Snowcubes'),
      url: normalizeString(product.url),
      brand: {
        '@type': 'Brand',
        name: 'Snowcubes',
      },
      aggregateRating: aggregateRating,
    });
    (doc.head || doc.documentElement).appendChild(node);
    return { updated: true, mode: 'created' };
  }

  var api = {
    buildAggregateRating: buildAggregateRating,
    containsAggregateRating: containsAggregateRating,
    upsertAggregateJsonLd: upsertAggregateJsonLd,
  };

  global.SnowcubesReviews = global.SnowcubesReviews || {};
  Object.assign(global.SnowcubesReviews, api);

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : globalThis);
